/**
 * Qualifying questions (DIM1 guidance).
 *
 * When an agent has `qualifying_questions` on and its DIM1 SOPs require a
 * detail the request did not supply, the backend answers with a question
 * instead of running. The question comes back through the *same* execution
 * endpoint the request went to, and the answer goes back the same way — there
 * is no /qualify or /answer endpoint. See the DIM1 UI integration guide §5–8.
 */

/** Frame/envelope `type` values that carry a qualification outcome. */
export const QUALIFICATION_REQUIRED = 'qualification_required';
export const QUALIFICATION_CANCELLED = 'qualification_cancelled';
export const QUALIFICATION_LIMIT_REACHED = 'qualification_limit_reached';

export const QUALIFICATION_TYPES = [
  QUALIFICATION_REQUIRED,
  QUALIFICATION_CANCELLED,
  QUALIFICATION_LIMIT_REACHED,
];

/** Backend default when `expires_in` is absent (seconds). */
const DEFAULT_EXPIRES_IN_SECONDS = 1800;

export const isQualificationType = (value) =>
  typeof value === 'string' && QUALIFICATION_TYPES.includes(value);

/**
 * A qualification outcome can arrive either as a bare SSE frame or wrapped in
 * the non-streaming `{ response: {...} }` envelope. Returns the inner object
 * when there is one, else null.
 */
export const extractQualificationEnvelope = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  if (isQualificationType(payload.type)) return payload;

  const inner = payload.response;
  if (inner && typeof inner === 'object' && isQualificationType(inner.type)) {
    // The outer envelope is the only place session_id is guaranteed on the
    // background endpoints, so carry it down.
    return inner.session_id ? inner : { ...inner, session_id: payload.session_id };
  }
  return null;
};

/**
 * Route a qualification outcome (question / cancelled / limit reached) to its
 * callback. Accepts a bare SSE frame or a non-streaming envelope, and returns
 * true when the payload was one — the caller then stops treating it as an answer.
 */
export const dispatchQualification = (payload, callbacks) => {
  if (!callbacks) return false;
  const frame = extractQualificationEnvelope(payload);
  if (!frame) return false;

  if (frame.type === QUALIFICATION_REQUIRED) {
    callbacks.onQualificationRequired?.(frame);
    return true;
  }
  if (frame.type === QUALIFICATION_CANCELLED) {
    callbacks.onQualificationCancelled?.(frame);
    return true;
  }
  if (frame.type === QUALIFICATION_LIMIT_REACHED) {
    callbacks.onQualificationLimitReached?.(frame);
    return true;
  }
  return false;
};

/**
 * Normalize a `qualification_required` frame into the shape the chat UI holds.
 * Optional fields get their documented defaults; unknown extras are dropped.
 */
export const normalizeQualificationQuestion = (frame, { receivedAt = Date.now() } = {}) => {
  if (!frame || typeof frame !== 'object') return null;

  const qualificationId = typeof frame.qualification_id === 'string' ? frame.qualification_id : '';
  if (!qualificationId) return null;

  const expiresInRaw = Number(frame.expires_in);
  const expiresIn =
    Number.isFinite(expiresInRaw) && expiresInRaw > 0 ? expiresInRaw : DEFAULT_EXPIRES_IN_SECONDS;

  return {
    qualificationId,
    question: typeof frame.question === 'string' ? frame.question : '',
    // Opaque backend key — echoed back verbatim in `answers`, never invented.
    field: typeof frame.field === 'string' ? frame.field : '',
    options: Array.isArray(frame.options)
      ? frame.options.map((option) => String(option ?? '')).filter((option) => option !== '')
      : [],
    // Currently always false; there is no skip-and-run action, only cancel.
    canSkip: frame.can_skip === true,
    sessionId: typeof frame.session_id === 'string' ? frame.session_id : null,
    resendAttachments: frame.resend_attachments === true,
    expiresIn,
    expiresAt: receivedAt + expiresIn * 1000,
    receivedAt,
  };
};

/**
 * Normalize a `qualification_limit_reached` frame. Both fields are optional.
 * `fields` are opaque backend keys (possibly generic, e.g. "clarification") —
 * kept for completeness but not shown to the user.
 */
export const normalizeQualificationLimit = (frame) => ({
  message:
    typeof frame?.message === 'string' && frame.message.trim() !== ''
      ? frame.message
      : 'Essential details are still missing. Start a new request including these details.',
  fields: Array.isArray(frame?.fields)
    ? frame.fields.map((entry) => String(entry ?? '')).filter((entry) => entry !== '')
    : [],
});

/** Answer/cancel body for the execution endpoints. `answers` is optional. */
export const buildQualificationPayload = ({ qualificationId, action = 'answer', answers = null }) => {
  if (!qualificationId) return null;

  const payload = { qualification_id: qualificationId, action };

  // At most 8 entries, each a nonempty string of at most 2000 characters.
  if (answers && typeof answers === 'object') {
    const entries = Object.entries(answers)
      .map(([key, value]) => [key, typeof value === 'string' ? value : String(value ?? '')])
      .filter(([key, value]) => key && value.trim() !== '')
      .slice(0, 8)
      .map(([key, value]) => [key, value.slice(0, 2000)]);

    if (entries.length > 0) {
      payload.answers = Object.fromEntries(entries);
    }
  }

  return payload;
};
